import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IS_RULE_ACTIVE } from '../../components/models/BlockArray/utils/consts'
import _isEqual from 'lodash/isEqual'
import { blockDictionaryActions } from '../actions/modelsActions/blockDictionaryActions'
import { blockArrayActions } from '../actions/modelsActions/blockArrayActions'
import { inputActions } from '../actions/modelsActions/inputActions'
import { ConfigurationsActionType } from '../../types/configurations'
import { initEmptyDataAction } from '../actions/modelsActions/initEmptyData'
import { validationActions } from '../actions/modelsActions/validationActions'

interface RuleCardState {
    index: number | undefined
    schema: Record<string, any>
    config: Record<string, any>
    configDefault: Record<string, any>
    fullPath: string[]
    invalidFields: Record<string, any>
    isUnsaved: boolean
    isChecked: number
}

const getInitialState = () => ({
    index: undefined,
    schema: {},
    config: {},
    configDefault: {},
    fullPath: [],
    invalidFields: {},
    isUnsaved: false,
    isChecked: 0,
})

const initialState: RuleCardState = getInitialState()

const ruleCardSlice = createSlice({
    name: 'ruleCardSlice',
    initialState,
    reducers: {
        resetRuleSchemaAndConfig: (state) => {
            state = getInitialState()
            return state
        },

        setRuleSchemaAndConfig: (
            state,
            action: PayloadAction<{
                index: number | undefined
                schema: Record<string, any>
                config: Record<string, any>
                fullPath: string[]
            }>
        ) => {
            const { index, schema, config, fullPath } = action.payload
            state.index = index
            state.schema = schema
            state.configDefault[index!] = config
            state.config[index!] = config
            state.fullPath = fullPath
        },

        checkRuleUnsavedChanges: (state) => {
            state.isUnsaved = !_isEqual(state.config, state.configDefault)
        },

        checkInvalidFields: (state) => {
            state.isChecked = state.isChecked + 1
        },

        setRuleConfigInvalid: (
            state,
            action: PayloadAction<{
                errorKeyName: string
            }>
        ) => {
            const { errorKeyName } = action.payload
            state.invalidFields[errorKeyName] = true
        },

        setRuleConfigValid: (
            state,
            action: PayloadAction<{
                errorKeyName: string
            }>
        ) => {
            const { errorKeyName } = action.payload
            delete state.invalidFields?.[errorKeyName]
        },

        disableRuleBlock: (
            state,
            action: PayloadAction<{
                path: string[]
                errorKeyName: string
            }>
        ) => {
            const { path, errorKeyName } = action.payload

            path.reduce((acc: any, cur: any, idx: number) => {
                if (idx === path.length - 1) {
                    if (acc) {
                        return (acc[cur] = null)
                    } else {
                        return acc
                    }
                }
                return acc[cur]
            }, state.config)

            validationActions.cleanRuleBlockInvalidFields(
                errorKeyName,
                state.invalidFields
            )
        },

        enableRuleBlock: (
            state,
            action: PayloadAction<{ path: string[]; data: Record<string, any> }>
        ) => {
            const { path, data } = action.payload
            path.reduce((acc: any, cur: any, idx: number) => {
                if (idx === path.length - 1) {
                    return (acc[cur] = data)
                }
                return acc[cur]
            }, state?.config)
        },

        initRuleEmptyData: (
            state,
            action: PayloadAction<{
                path: string[]
                name: string
                schema: Record<string, any>
            }>
        ) => {
            initEmptyDataAction(
                state.config,
                action.payload.path,
                action.payload.name,
                action.payload.schema
            )
        },

        modifyRuleCardConfig: (
            state,
            action: PayloadAction<{ path: string[]; data: any }>
        ) => {
            const { path, data } = action.payload
            const lastIndex = path.length - 1
            const isArray = Array.isArray(data)

            path.reduce((acc: any, cur: string, idx: number) => {
                if (
                    idx === lastIndex &&
                    data &&
                    Object.keys(data).includes(cur)
                ) {
                    acc[cur] = data[cur]
                }
                if (idx === lastIndex && data && isArray) {
                    acc[cur] = data
                }
                if (idx !== lastIndex && acc[cur] === undefined) {
                    acc[cur] = {}
                }
                return acc[cur]
            }, state.config)
        },

        modifyRuleInput: (
            state,
            action: PayloadAction<{
                path: string[]
                componentState: any
                data: any
                name: string
            }>
        ) => {
            inputActions.inputModify(
                state.config,
                action.payload.path,
                action.payload.componentState,
                action.payload.data,
                action.payload.name
            )
        },

        setRuleCardRuleName: (
            state,
            action: PayloadAction<{ path: string[]; ruleName: string }>
        ) => {
            const { path, ruleName } = action.payload
            inputActions.ruleNameModify(state.config, path, ruleName)
        },

        modifyRuleArrayScalarValues: (
            state,
            action: PayloadAction<{
                path: string[]
                componentState: any
                name: string
                isEmptyAsNull?: boolean
            }>
        ) => {
            inputActions.multiSelectModify(
                state.config,
                action.payload.path,
                action.payload.componentState,
                action.payload.name,
                action.payload.isEmptyAsNull
            )
        },

        addItemToArrayEnd: (
            state,
            action: PayloadAction<{
                path: string[]
                data: any
            }>
        ) => {
            blockArrayActions.addItem(
                state.config,
                action.payload.path,
                action.payload.data,
                1,
                ConfigurationsActionType.BLOCK_ARRAY_CARD_ADD_TO_END
            )
        },

        addItemToArrayBeginning: (
            state,
            action: PayloadAction<{
                path: string[]
                data: any
            }>
        ) => {
            blockArrayActions.addItem(
                state.config,
                action.payload.path,
                action.payload.data,
                1,
                ConfigurationsActionType.BLOCK_ARRAY_CARD_ADD_TO_BEGINNING
            )
        },

        duplicateRuleBlockArrayCard: (
            state,
            action: PayloadAction<{
                path: string[]
                data: { index: number; item: any }
            }>
        ) => {
            blockArrayActions.addItem(
                state.config,
                action.payload.path,
                action.payload.data,
                1,
                ConfigurationsActionType.BLOCK_ARRAY_CARD_DUPLICATE
            )
        },

        deleteRuleBlockArrayItem: (
            state,
            action: PayloadAction<{ path: string[]; index: number }>
        ) => {
            blockArrayActions.deleteItem(
                state.config,
                action.payload.path,
                action.payload.index
            )
        },

        deleteRuleBlockArrayCardItem: (
            state,
            action: PayloadAction<{
                path: string[]
                data: any[]
                errorKeyName: string
            }>
        ) => {
            const { path, data, errorKeyName } = action.payload
            blockArrayActions.deleteItemCard(state.config, path, data)

            validationActions.cleanRuleBlockInvalidFields(
                errorKeyName,
                state.invalidFields
            )
        },

        addRuleBlockDictItem: (
            state,
            action: PayloadAction<{ path: string[]; item: any; data: any }>
        ) => {
            const { path, item, data } = action.payload

            blockDictionaryActions.addItem(state.config, path, item, data)
        },

        deleteRuleBlockDictItem: (
            state,
            action: PayloadAction<{
                path: string[]
                data: any
                dictionaryKey: string
            }>
        ) => {
            const { path, data, dictionaryKey } = action.payload

            blockDictionaryActions.deleteItem(
                state.config,
                path,
                data,
                dictionaryKey
            )
        },

        addRuleBlockDictNewKey: (
            state,
            action: PayloadAction<{ path: string[]; oldKey: string; data: any }>
        ) => {
            const { path, oldKey, data } = action.payload

            blockDictionaryActions.newKey(state.config, path, data, oldKey)
        },

        editBlockDictionaryValue: (
            state,
            action: PayloadAction<{ path: string[]; key: any; value: any }>
        ) => {
            const { path, key, value } = action.payload
            blockDictionaryActions.editDictionaryValue(
                state?.config ?? {},
                path,
                key,
                value
            )
        },

        changeRuleCardState: (state, action: PayloadAction<boolean>) => {
            state.config[state.index!][IS_RULE_ACTIVE] = action.payload
        },
    },
})

export const { actions: ruleCardActions } = ruleCardSlice
export const { reducer: ruleCardReducer } = ruleCardSlice
